// Гараж, машина и выезды — tech/03 §5; публичный профиль и визит в гости — tech/03 §3 (E06-T01, T02, T11, T13, T03).
// Числа (цены, бонусы, длительности) — конфиги сервера `garage.*`, `car`, `trips` (ADR-0010); здесь — формы.
import { z } from 'zod';
import { Grant, IsoTime, PublicId, Uuid7, ZoneId, strict } from '../common.js';
import { endpoint } from '../endpoint.js';
import { CoopBrief, Item, PlayerBrief, Wallet } from './models.js';

/** Узел машины: двигатель C1, кузов C2, ходовая C3 (ГД 04 §2, X-12). */
export const CarNodeId = z.enum(['engine', 'body', 'chassis']);
/** Маршрут выезда R1… (ГД 04 §3). */
export const TripDefId = z.string().regex(/^R[0-9]{1,2}$/);

export const GarageZone = strict({
  zoneId: ZoneId,
  level: z.int().min(0).max(5),
  /** Идёт стройка следующего уровня — когда закончится. */
  upgradeFinishAt: IsoTime.nullable(),
  /** Цена следующего уровня; null — потолок. */
  nextCost: strict({ nuts: z.int().nonnegative(), timeS: z.int().nonnegative(), playerLevel: z.int().positive() }).nullable(),
  /** Бонус текущего уровня по ключам `garage.zones` (ГД 03 §2): `attack_pct`, `hp_pct`, `crit_chance_pp`… */
  bonus: z.record(z.string(), z.number()),
  /** Бонус следующего уровня (те же ключи); null — потолок (E06-T06, Р-59). Новые поля необязательны в схеме ради старых моков клиента; сервер отдаёт их всегда. */
  nextBonus: z.record(z.string(), z.number()).nullable().optional(),
  /** Начало идущей стройки — окно отмены 5 мин; null — стройки нет. */
  upgradeStartedAt: IsoTime.nullable().optional(),
  /** Цена ускорения идущей стройки в талонах по остатку на момент ответа; null — стройки нет. */
  speedupTalons: z.int().nonnegative().nullable().optional(),
});
export type GarageZone = z.infer<typeof GarageZone>;

const ZoneParams = strict({ zoneId: ZoneId });
const ZoneWallet = strict({ zone: GarageZone, wallet: Wallet });

export const getGarage = endpoint({
  id: 'garage.get', method: 'GET', path: '/garage', auth: true, signed: false, idempotency: 'read', rate: 'read',
  response: strict({ zones: z.array(GarageZone) }), status: 200, errors: [], spec: 'tech/03 §5; ГД 03', task: 'E06-T01',
});

export const upgradeZone = endpoint({
  id: 'garage.zone.upgrade', method: 'POST', path: '/garage/zones/{zoneId}/upgrade', auth: true, signed: true, idempotency: 'key-required', rate: 'write',
  params: ZoneParams, body: strict({}), response: ZoneWallet, status: 200,
  errors: ['NOT_ENOUGH_NUTS', 'MAX_LEVEL', 'REQUIREMENT_NOT_MET', 'CONFLICT'], spec: 'tech/03 §5; ГД 03 §3', task: 'E06-T01',
});

export const speedupZone = endpoint({
  id: 'garage.zone.speedup', method: 'POST', path: '/garage/zones/{zoneId}/speedup', auth: true, signed: true, idempotency: 'key-required', rate: 'write',
  params: ZoneParams, body: strict({ payWith: z.literal('talons') }), response: ZoneWallet, status: 200,
  errors: ['NOT_ENOUGH_TALONS', 'TALONS_DEBT', 'NOT_UPGRADING'], spec: 'tech/03 §5; ГД 03 §3', task: 'E06-T01',
});

export const cancelZone = endpoint({
  id: 'garage.zone.cancel', method: 'POST', path: '/garage/zones/{zoneId}/cancel', auth: true, signed: true, idempotency: 'key-required', rate: 'write',
  params: ZoneParams, body: strict({}), response: ZoneWallet.extend({ refunded: Grant }), status: 200,
  errors: ['NOT_UPGRADING', 'CANCEL_WINDOW_PASSED'], spec: 'tech/03 §5; ГД 03 §3', task: 'E06-T01',
});

export const CarNode = strict({
  nodeId: CarNodeId,
  level: z.int().min(0).max(5),
  /** Состояние 0–100% (ГД 04 §2.2). */
  condition: z.int().min(0).max(100),
  /** Цена следующего уровня; null — потолок. */
  upgradeCost: strict({ nuts: z.int().nonnegative(), scrap: z.int().nonnegative(), playerLevel: z.int().positive() }).nullable(),
  /** «Подлатать до 100%» (economy/02 §6.1); 0 — узел цел или уровня 0. */
  patchCost: z.int().nonnegative(),
});
export type CarNode = z.infer<typeof CarNode>;

const NodeParams = strict({ nodeId: CarNodeId });
const ExpectedNuts = strict({ expectedCostNuts: z.int().nonnegative() });

export const getCar = endpoint({
  id: 'car.get', method: 'GET', path: '/car', auth: true, signed: false, idempotency: 'read', rate: 'read',
  response: strict({ nodes: z.array(CarNode), patchAllCost: z.int().nonnegative(), tripsAvailable: z.boolean() }),
  status: 200, errors: [], spec: 'tech/03 §5; ГД 04 §2', task: 'E06-T13',
});

export const upgradeNode = endpoint({
  id: 'car.node.upgrade', method: 'POST', path: '/car/nodes/{nodeId}/upgrade', auth: true, signed: true, idempotency: 'key-required', rate: 'write',
  params: NodeParams, body: ExpectedNuts, response: strict({ node: CarNode, wallet: Wallet }), status: 200,
  errors: ['PRICE_CHANGED', 'TRIP_RUNNING', 'NOT_ENOUGH_NUTS', 'NOT_ENOUGH_SCRAP', 'MAX_LEVEL', 'LEVEL_TOO_LOW'], spec: 'tech/03 §5; ГД 04 §2.1', task: 'E06-T13',
});

export const patchNode = endpoint({
  id: 'car.node.patch', method: 'POST', path: '/car/nodes/{nodeId}/patch', auth: true, signed: true, idempotency: 'key-required', rate: 'write',
  params: NodeParams, body: ExpectedNuts, response: strict({ node: CarNode, wallet: Wallet }), status: 200,
  errors: ['PRICE_CHANGED', 'TRIP_RUNNING', 'NOT_ENOUGH_NUTS', 'ALREADY_FULL', 'REQUIREMENT_NOT_MET'], spec: 'tech/03 §5; ГД 04 §2.2; economy/02 §6.1', task: 'E06-T13',
});

export const patchAll = endpoint({
  id: 'car.patch_all', method: 'POST', path: '/car/patch-all', auth: true, signed: true, idempotency: 'key-required', rate: 'write',
  body: ExpectedNuts, response: strict({ nodes: z.array(CarNode), wallet: Wallet }), status: 200,
  errors: ['PRICE_CHANGED', 'TRIP_RUNNING', 'NOT_ENOUGH_NUTS', 'ALREADY_FULL'], spec: 'tech/03 §5; ux/06 §1', task: 'E06-T13',
});

/** Маршрут для экрана выбора (ГД 04 §3): длительность и награда — уже с бонусами игрока. */
export const TripDef = strict({
  tripDefId: TripDefId,
  available: z.boolean(),
  /** Почему недоступен: `level`, `district`, `node_level`, `node_condition`, `disabled` (маршрут вне MVP). */
  lockedReason: z.string().max(32).optional(),
  durationS: z.int().positive(),
  needs: z.partialRecord(CarNodeId, z.int().positive()),
  wearPct: z.int().nonnegative(),
  riskPct: z.number().min(0).max(100),
  reward: Grant,
});

export const Trip = strict({
  tripId: Uuid7,
  tripDefId: TripDefId,
  startedAt: IsoTime,
  finishAt: IsoTime,
  status: z.enum(['running', 'done', 'claimed']),
  /** Итог раскрывается после возвращения машины: награда и была ли поломка. */
  result: strict({ rewards: Grant, broken: z.boolean() }).optional(),
  /**
   * «Вернуть сейчас» (ГД 04 §4 п. 5, E06-T07, Р-59): цена в талонах по остатку и можно ли сейчас. `reason`:
   * `short` — маршрут короче 2 ч, `daily` — сегодня уже ускоряли. null — машина не в пути.
   */
  speedup: strict({ talons: z.int().nonnegative(), available: z.boolean(), reason: z.enum(['short', 'daily']).optional() }).nullable().optional(),
});
export type Trip = z.infer<typeof Trip>;

const TripParams = strict({ tripId: Uuid7 });

export const getTrips = endpoint({
  id: 'car.trips', method: 'GET', path: '/car/trips', auth: true, signed: false, idempotency: 'read', rate: 'read',
  response: strict({ available: z.array(TripDef), running: Trip.nullable() }), status: 200, errors: [], spec: 'tech/03 §5; ГД 04 §3', task: 'E06-T02',
});

export const startTrip = endpoint({
  id: 'car.trip.start', method: 'POST', path: '/car/trips', auth: true, signed: true, idempotency: 'key-required', rate: 'write',
  body: strict({ tripDefId: TripDefId }), response: strict({ trip: Trip }), status: 201,
  errors: ['TRIP_RUNNING', 'CAR_NOT_READY', 'REQUIREMENT_NOT_MET', 'LEVEL_TOO_LOW'], spec: 'tech/03 §5; ГД 04 §4 (энергию не тратит)', task: 'E06-T02',
});

export const speedupTrip = endpoint({
  id: 'car.trip.speedup', method: 'POST', path: '/car/trips/{tripId}/speedup', auth: true, signed: true, idempotency: 'key-required', rate: 'write',
  params: TripParams, body: strict({ expectedTalons: z.int().nonnegative() }), response: strict({ trip: Trip, wallet: Wallet }), status: 200,
  errors: ['TALONS_DEBT', 'NOT_RUNNING', 'CONFLICT', 'NOT_ENOUGH_TALONS', 'DAILY_LIMIT', 'REQUIREMENT_NOT_MET'], spec: 'tech/03 §5; ГД 04 §4 п. 5', task: 'E06-T11',
});

export const claimTrip = endpoint({
  id: 'car.trip.claim', method: 'POST', path: '/car/trips/{tripId}/claim', auth: true, signed: true, idempotency: 'natural', rate: 'write',
  params: TripParams, body: strict({}), response: strict({ rewards: Grant, broken: z.boolean(), wallet: Wallet }), status: 200,
  errors: ['NOT_FOUND', 'NOT_FINISHED', 'ALREADY_CLAIMED'], spec: 'tech/03 §5; ГД 04 §4 п. 3–4', task: 'E06-T02',
});

/** Публичный профиль (ГД 14 §5): шапка, гараж, машина, кооператив, статистика. Без сумки, валют, покупок и почты. */
export const PublicProfile = strict({
  player: PlayerBrief,
  appearance: z.record(z.string(), z.string()),
  /** Надетая экипировка — аватар в экипировке (видна всем). */
  equipped: z.array(Item),
  garage: strict({ zones: z.array(strict({ zoneId: ZoneId, level: z.int().min(0).max(5) })) }),
  car: strict({ nodes: z.array(strict({ nodeId: CarNodeId, level: z.int().min(0).max(5) })) }),
  coop: CoopBrief.nullable(),
  stats: strict({
    respectTotal: z.int().nonnegative(),
    respectMonth: z.int().nonnegative(),
    bossesDefeated: z.int().nonnegative(),
    bestWeekDamage: z.int().nonnegative(),
  }),
  /** Смотрящий уже уважил этого игрока в текущие игровые сутки (E06-T08, Р-59). */
  respectedToday: z.boolean().optional(),
  /** Дружба со смотрящим: `outgoing` — заявка отправлена, `incoming` — пришла от него, `self` — свой профиль. */
  friendStatus: z.enum(['none', 'outgoing', 'incoming', 'friends', 'self']).optional(),
});

const PlayerParams = strict({ publicId: PublicId });

export const getPlayer = endpoint({
  id: 'player.public', method: 'GET', path: '/players/{publicId}', auth: true, signed: false, idempotency: 'read', rate: 'read',
  params: PlayerParams, response: PublicProfile, status: 200, errors: ['NOT_FOUND'], spec: 'tech/03 §3; ГД 14 §5', task: 'E06-T03',
});

/**
 * «Зайти в гости» (ГД 03 §2 «Лавочка у входа», `SCR-GARAGE-VISIT`): гостю — бонус опыта на время, не больше раза в
 * сутки от одного хозяина и не больше трёх хозяев в сутки; только друг или сосед по кооперативу. Решение Р-51.
 */
export const visitGarage = endpoint({
  id: 'player.visit', method: 'POST', path: '/players/{publicId}/visit', auth: true, signed: true, idempotency: 'natural', rate: 'write',
  params: PlayerParams, body: strict({}),
  response: strict({ bonus: strict({ xpPct: z.number().positive(), until: IsoTime }).nullable(), reason: z.enum(['not_neighbor', 'already_today', 'hosts_limit', 'no_bench', 'self']).optional() }),
  status: 200, errors: ['NOT_FOUND'], spec: 'ГД 03 §2; ГД 14 §5', task: 'E06-T03',
});
